/**
 * Составной адрес (ТЗ п. 5.2.1, форма приказа МЭР № 530).
 *
 * ГРАНИЦА ИНТЕГРАЦИИ. Адресных подсказок здесь нет. Ввод — по составным
 * полям формы, а строка для заявления собирается из них автоматически.
 * Единственное исключение — подбор индекса (решение заказчика 25.10.2026):
 * через сервер в DaData уходят части адреса до дома, без квартиры и ФИО.
 *
 * Субъект РФ выбирается из списка (с поиском по набранному), а не вводится
 * произвольным текстом: по нему подставляется арбитражный суд (правило R-01),
 * и опечатка здесь стоила бы неверной подсудности. Список берётся из того же справочника, откуда берутся суды,
 * поэтому «субъект есть, а суда по нему нет» невозможно.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { composeAddress, setAddressFull, updateAddressPart, type Address } from '@bd/domain';
import type { PostalCodeParts } from '../../api.js';
import { Field } from './index.js';

export interface RegionOption {
  code: string;
  name: string;
}

export interface AddressFieldProps {
  label: string;
  value: Address;
  onChange: (address: Address) => void;
  required?: boolean;
  error?: string | null;
  readOnly?: boolean;
  /** Субъекты РФ из справочника судов */
  regions: RegionOption[];
  /** Код субъекта — по нему предзаполняется суд (правило R-01) */
  onRegionDetected?: (regionCode: string) => void;
  /** Подбор индекса по адресу (DaData, решение заказчика 25.10.2026); `null` — дом не найден */
  lookupPostalCode?: (parts: PostalCodeParts) => Promise<string | null>;
}

type PostalStatus = '' | 'searching' | 'found' | 'not-found' | 'incomplete' | 'po-box' | 'error';

const POSTAL_HINTS: Record<PostalStatus, string> = {
  '': '',
  searching: 'Ищу индекс по адресу…',
  found: 'Подставлен по адресу — проверьте',
  'not-found': 'Дом по этому адресу не найден — впишите индекс вручную',
  incomplete: 'Для подбора нужны субъект, город или населённый пункт, улица и дом',
  'po-box': 'У абонентского ящика свой индекс почтового отделения — впишите его вручную',
  error: 'Не удалось подобрать индекс — впишите вручную',
};

/** Абонентский ящик: индекс у него — индекс почтового отделения, по улице его не подобрать. */
export const isPoBox = (a: Address) => /а\s*\/\s*я|абонентск/i.test([a.street, a.house, a.full].filter(Boolean).join(' '));

/** Части адреса, от которых зависит индекс. Квартира — нет, и она не передаётся. */
export const postalParts = (a: Address): PostalCodeParts => ({
  region: a.region ?? '',
  area: a.area ?? '',
  city: a.city ?? '',
  settlement: a.settlement ?? '',
  street: a.street ?? '',
  house: a.house ?? '',
});

/** Части адреса в порядке формы приказа № 530. */
const PARTS: Array<[key: keyof Address, label: string, hint?: string]> = [
  ['postalCode', 'Индекс'],
  ['area', 'Район'],
  ['city', 'Город'],
  ['settlement', 'Населённый пункт', 'село, посёлок и так далее'],
  ['street', 'Улица', 'проспект, переулок и так далее'],
  ['house', 'Дом (владение)'],
  ['block', 'Корпус (строение)'],
  ['flat', 'Квартира (офис)'],
];

/** Строка для сравнения: регистр, «ё», знаки препинания не важны. */
const normalize = (s: string) => s.toLowerCase().replace(/ё/g, 'е').replace(/[^a-zа-я0-9]+/g, ' ').trim();

/**
 * Субъекты, подходящие под набранное. Каждое слово запроса должно быть
 * началом какого-нибудь слова названия: «башк» → «Республика Башкортостан»,
 * «моск обл» → «Московская область». Сначала — те, что начинаются с запроса.
 */
export function matchRegions(regions: RegionOption[], query: string): RegionOption[] {
  const words = normalize(query).split(' ').filter(Boolean);
  if (words.length === 0) return regions;
  const matched = regions.filter((region) => {
    const nameWords = normalize(region.name).split(' ');
    return words.every((word) => nameWords.some((nameWord) => nameWord.startsWith(word)));
  });
  const first = normalize(query);
  return [...matched.filter((r) => normalize(r.name).startsWith(first)), ...matched.filter((r) => !normalize(r.name).startsWith(first))];
}

/**
 * Выбор субъекта с поиском по списку (просьба заказчика 25.10.2026).
 *
 * Ввести можно только субъект ИЗ СПИСКА: по нему подставляется арбитражный суд,
 * и произвольный текст дал бы неверную подсудность. Набранное, но не выбранное,
 * при уходе с поля либо подставляется (если подходит ровно один субъект),
 * либо откатывается к прежнему значению.
 */
function RegionPicker({
  regions,
  value,
  readOnly,
  onPick,
}: {
  regions: RegionOption[];
  value: string;
  readOnly?: boolean;
  onPick: (code: string) => void;
}) {
  const [query, setQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const matches = useMemo(() => matchRegions(regions, query ?? ''), [regions, query]);

  const choose = (region: RegionOption | undefined) => {
    if (region) onPick(region.code);
    setQuery(null);
    setOpen(false);
  };

  const finish = () => {
    if (query === null) return setOpen(false);
    if (!query.trim()) return choose(undefined); // стёрли — значение остаётся прежним
    const exact = regions.find((r) => normalize(r.name) === normalize(query));
    choose(exact ?? (matches.length === 1 ? matches[0] : undefined));
  };

  // Подсвеченный пункт держим в видимой части списка
  useEffect(() => {
    listRef.current?.children[active]?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  return (
    <div className="cell-with-suggestions">
      <input
        value={query ?? value}
        readOnly={readOnly}
        placeholder="— выберите субъект —"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        onFocus={(e) => {
          if (readOnly) return;
          e.target.select();
          setOpen(true);
          setActive(0);
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onBlur={finish}
        onKeyDown={(e) => {
          if (readOnly) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setActive((i) => Math.min(i + 1, matches.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (open && matches[active]) choose(matches[active]);
          } else if (e.key === 'Escape') {
            setQuery(null);
            setOpen(false);
          }
        }}
      />
      {open && !readOnly && (
        <ul className="suggestions" role="listbox" ref={listRef}>
          {matches.length === 0 && <li className="hint" style={{ padding: '5px 8px' }}>Такого субъекта нет в списке</li>}
          {matches.map((region, i) => (
            <li key={region.code} role="option" aria-selected={i === active}>
              <button
                type="button"
                className={i === active ? 'suggestion-active' : undefined}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(region)}
              >
                {region.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AddressField({
  label,
  value,
  onChange,
  required,
  error,
  readOnly,
  regions,
  onRegionDetected,
  lookupPostalCode,
}: AddressFieldProps) {
  const setPart = (key: keyof Address, partValue: string) => onChange(updateAddressPart(value, key, partValue));

  /*
    Индекс подбирается сам, когда юрист уходит с поля «Дом», а индекс пуст;
    кнопкой — в любой момент. Ответ приходит не сразу: подставляется он,
    только если адрес за это время не поменяли, и от последнего значения,
    а не от того, что было при запросе, — иначе затёрлись бы новые правки.
  */
  const [postalStatus, setPostalStatus] = useState<PostalStatus>('');
  const latest = useRef(value);
  latest.current = value;

  const findPostalCode = async (manual: boolean) => {
    const current = latest.current;
    const parts = postalParts(current);
    if (!lookupPostalCode || readOnly) return;
    if (!manual && current.postalCode) return;
    // «а/я 238» DaData принимает за улицу и дом и даёт общий индекс города (сверка 26.10.2026)
    if (isPoBox(current)) {
      if (manual) setPostalStatus('po-box');
      return;
    }
    if (!parts.region || !(parts.city || parts.settlement) || !parts.street || !parts.house) {
      if (manual) setPostalStatus('incomplete');
      return;
    }
    const key = JSON.stringify(parts);
    setPostalStatus('searching');
    try {
      const postalCode = await lookupPostalCode(parts);
      const now = latest.current;
      if (JSON.stringify(postalParts(now)) !== key) return setPostalStatus('');
      if (!postalCode) return setPostalStatus('not-found');
      if (manual || !now.postalCode) onChange(updateAddressPart(now, 'postalCode', postalCode));
      setPostalStatus('found');
    } catch {
      // Без DaData поле просто остаётся ручным; ошибку показываем только на кнопку
      setPostalStatus(manual ? 'error' : '');
    }
  };

  const pickRegion = (code: string) => {
    const region = regions.find((r) => r.code === code);
    onChange(updateAddressPart(value, 'region', region?.name ?? ''));
    if (code) onRegionDetected?.(code);
  };

  return (
    <div className="address-field">
      <Field
        label={label}
        error={error}
        required={required}
        hint={
          value.fullManual
            ? 'Строка правилась вручную и больше не пересобирается из полей'
            : 'Собирается из полей ниже. Можно поправить — тогда автосборка отключится'
        }
      >
        <div className="field-row">
          <input
            value={value.full ?? ''}
            readOnly={readOnly}
            placeholder="Заполните поля ниже — строка соберётся сама"
            onChange={(e) => onChange(setAddressFull(value, e.target.value))}
          />
          {value.fullManual && (
            <button
              type="button"
              className="link-button"
              disabled={readOnly}
              title="Собрать строку из составных полей заново"
              onClick={() => onChange({ ...value, full: composeAddress(value), fullManual: false })}
            >
              Собрать заново
            </button>
          )}
        </div>
      </Field>

      <div className="address-parts">
        <div className="field field-small address-region">
          <span className="field-label">
            Субъект Российской Федерации
            <span className="required" title="Обязательное поле"> *</span>
          </span>
          <RegionPicker regions={regions} value={value.region ?? ''} readOnly={readOnly} onPick={pickRegion} />
          <span className="field-hint">Начните вводить название или выберите из списка. По субъекту подставляется арбитражный суд</span>
        </div>

        {PARTS.map(([key, partLabel, hint]) => (
          <label className="field field-small" key={key}>
            <span className="field-label">
              {partLabel}
              {key === 'postalCode' && lookupPostalCode && !readOnly && (
                <button
                  type="button"
                  className="link-button"
                  disabled={postalStatus === 'searching'}
                  onClick={(e) => {
                    e.preventDefault();
                    void findPostalCode(true);
                  }}
                >
                  {' '}подобрать
                </button>
              )}
            </span>
            <input
              value={(value[key] as string) ?? ''}
              readOnly={readOnly}
              onChange={(e) => {
                setPart(key, e.target.value);
                if (key === 'postalCode') setPostalStatus('');
              }}
              onBlur={key === 'house' ? () => void findPostalCode(false) : undefined}
            />
            {key === 'postalCode' && postalStatus ? (
              <span className={postalStatus === 'not-found' || postalStatus === 'error' ? 'field-hint warning' : 'field-hint'}>
                {POSTAL_HINTS[postalStatus]}
              </span>
            ) : (
              hint && <span className="field-hint">{hint}</span>
            )}
          </label>
        ))}
      </div>
    </div>
  );
}
